import { useEffect, useState } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import Progress from "../../components/progress/Progress";
import NestedList, { Row } from "../../components/list/NestedList";
import { useTranslation } from "react-i18next";
import useInOutRoutes from "./useInOutRoutes";
import moment from "moment";
import ImageCarousel from "../../components/carousel/ImageCarousel";
import ModalCard from "../../components/modal/ModalCard";
import { STRING_SPLIT_CHAR } from "../../utils/stringUtils";
import DownloadNestedList from "../../utils/download/downloadNestedList";
import Toolbar from "../../components/toolbar/Toolbar";
import SearchBarPropertyGroups from "../../components/searchbar/SearchBarPropertyGroups";
import useWorkRoute from "./useWorkRoute";

const WorkingRoadPage = () => {
    const [isFilterActive, setIsFilterActive] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [scanComment, setScanComment] = useState<string>();
    const [scanImages, setScanImages] = useState<string[]>([]);

    const onOpen = (data: string[]) => {
        if (data.length === 0) return;

        const comment = data[0];
        const imagesKeys = data[1] ? data[1].split(STRING_SPLIT_CHAR) : [];

        setScanComment(comment);
        setScanImages(imagesKeys);
        setIsModalOpen(true)
    };

    const onClose = () => {
        setIsModalOpen(false);
        setScanComment("");
        setScanImages([]);
    };

    const { inOutRouteRows, setInOutRoutes, employees, selectedEmployees, setSelectedEmployees } = useInOutRoutes();
    const [formattedRows, setFormattedRows] = useState<Row[]>([]);
    const {
        start,
        setStart,
        end,
        setEnd,
        propertyGroupId,
        setPropertyGroupId,
        propertyGroups,
    } = useWorkRoute();

    const { t, i18n } = useTranslation();

    const columns = [
        t("header.startTime"),
        t("table.building.name"),
        t("header.floor"),
        t("header.room"),
        t("header.totalTime"),
        t("table.employeeFeedback.comment"),
    ]

    const downloadTable = new DownloadNestedList(t("data.workingRoads"), formattedRows, columns, t("header.summary"), t("header.details"),
        t("header.noData"), t("header.fileInfos"), t("header.exportedBy"), t("header.date"), t("header.hour"), t("header.filters"), t("table.property.propertyGroup"), t("header.employee"), t("header.totalHours"), t("header.shift"),
        [
            `${moment.utc(start).local().format("LLL")} ${t("header.to")} ${moment.utc(end).local().format("LLL")}`,
            `${propertyGroups.find((b) => b.id === propertyGroupId)?.name}`,
            selectedEmployees.length > 0 ? `${employees.filter((e) => selectedEmployees.includes(e.id)).map((e) => `${e.name}`).join(", ")}` : t("header.all")
        ],
    )

    function formatInOutRouteRows(rows: Row[]): Row[] {
        return rows.map((row) => {
            const formattedRow: Row = {
                ...row,
                items: row.items.map((item) => (typeof item === "number" ? `${item.toFixed(1)} ${t("header.hours")}` : item))
            };

            if (row.internalNodes)
                formattedRow.internalNodes = formatInOutRouteRows(row.internalNodes);

            return formattedRow;
        });
    }
    useEffect(() => {
        setFormattedRows(formatInOutRouteRows(inOutRouteRows));
    }, [inOutRouteRows, i18n.language]);

    useEffect(() => {
        setInOutRoutes(propertyGroupId, start, end, selectedEmployees);
    }, [propertyGroupId, start, end, selectedEmployees]);


    return (
        <>
            <Toolbar isFilterActive={isFilterActive} onFilterClick={() => setIsFilterActive(!isFilterActive)}
                downloadButtons={[
                    { label: "PDF", onClick: () => downloadTable.GetPDF() },
                    { label: "Excel", onClick: () => downloadTable.GetExcel(), },
                ]} />

            {isFilterActive && (
                <SearchBarPropertyGroups
                    propertyGroups={propertyGroups}
                    setPropertyGroupId={setPropertyGroupId}
                    propertyGroupId={propertyGroupId}
                    employees={employees}
                    selectedEmployees={selectedEmployees}
                    setSelectedEmployees={setSelectedEmployees}
                    start={start}
                    setStart={setStart}
                    end={end}
                    setEnd={setEnd}
                />
            )}

            <Box sx={{ marginTop: 2 }}>
                {inOutRouteRows.length > 0 ? (
                    <NestedList
                        rows={formattedRows}
                        columns={columns}
                        noDataText={t("header.noData")}
                        hasModal={true}
                        onModalOpen={onOpen}
                    />
                ) :
                    <Typography
                        sx={{ backgroundColor: (theme) => theme.palette.background.paper, padding: 2, textAlign: "center" }}>
                        {t("header.noData")}
                    </Typography>
                }
            </Box>
            <Dialog keepMounted open={isModalOpen} onClose={onClose} sx={{
                height: "100%",
                overflow: "auto",
            }}>
                <ModalCard title={t("table.employeeFeedback.title")} closeCallback={onClose} minWidth={600}>
                    <ModalCard.Main>
                        <Box >
                            {scanComment && <Typography sx={{ my: 2, whiteSpace: "pre-wrap" }}>
                                <b>{t("table.employeeFeedback.comment")}:</b> {scanComment}
                            </Typography>}
                            {scanImages.length > 0 &&
                                <ImageCarousel sx={{ width: "60%", py: 2, margin: "auto" }}>
                                    {scanImages.map((image, index) => (
                                        <img
                                            key={index}
                                            src={image}
                                            alt={`image-${index}`}
                                        />
                                    ))}
                                </ImageCarousel>
                            }
                        </Box>
                    </ModalCard.Main>
                </ModalCard>
            </Dialog>
        </>
    );
};

export default WorkingRoadPage;